import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */

  /*
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };
  */

   /*
   * Custome code
   */

  #handleProductSelect = (event) => {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

  // Loader ON
  this.classList.add('variant-image-loading');

  event.promise
    .then(({ detail }) => {
      if (!detail?.html) {
        this.classList.remove('variant-image-loading');
        return;
      }

      const newMediaGallery = detail.html.querySelector('media-gallery');

      if (!newMediaGallery) {
        this.classList.remove('variant-image-loading');
        return;
      }

      // Keep loader on new gallery
      newMediaGallery.classList.add('variant-image-loading');

      // Replace gallery
      this.replaceWith(newMediaGallery);

      /*
       * Wait only for the first/main product image.
       * Do NOT wait for thumbnails.
       */
      const mainImage = newMediaGallery.querySelector('img');

      if (!mainImage) {
        setTimeout(() => {
          newMediaGallery.classList.remove('variant-image-loading');
        }, 500);
        return;
      }

      const hideLoader = () => {
        setTimeout(() => {
          newMediaGallery.classList.remove('variant-image-loading');
        }, 150);
      };

      /*
       * Image already loaded
       */
      if (mainImage.complete && mainImage.naturalWidth > 0) {
        hideLoader();
        return;
      }

      /*
       * Wait for main image
       */
      mainImage.addEventListener('load', hideLoader, {
        once: true
      });

      mainImage.addEventListener('error', hideLoader, {
        once: true
      });

      /*
       * Safety fallback
       */
      setTimeout(() => {
        newMediaGallery.classList.remove('variant-image-loading');
      }, 2500);
    })
    .catch((error) => {
      this.classList.remove('variant-image-loading');

      if (error?.name !== 'AbortError') {
        console.warn(
          '[media-gallery] Event promise rejected:',
          error
        );
      }
    });
};

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
